import React, { useState } from 'react';
import { getAccentColor } from '../lib/jlfcTheme';
import {
  Alert,
  Box,
  Button,
  Paper,
  PasswordInput,
  Stack,
  Text,
  Title,
  Anchor,
  Group,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft } from '@tabler/icons-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { SaveButton } from '../components/FormActionButtons';
import classes from './LoginPage.module.css';
import { isMobilePhone } from '../lib/device';
import MobileUnsupportedNotice from '../components/MobileUnsupportedNotice';

export default function ResetPasswordPage() {
  const mobileBlocked = isMobilePhone();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const form = useForm({
    initialValues: { password: '', confirm: '' },
    validate: {
      password: (v) => {
        if (!v) return 'Mot de passe requis';
        if (v.length < 4) return 'Au moins 4 caractères';
        return null;
      },
      confirm: (v, values) =>
        v !== values.password ? 'Les mots de passe ne correspondent pas' : null,
    },
  });

  const handleSubmit = form.onSubmit(async (values) => {
    if (!token) {
      notifications.show({
        title: 'Lien invalide',
        message: 'Demandez un nouveau lien de réinitialisation.',
        color: 'red',
      });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: values.password }),
      });
      const data = await res.json();
      if (data.success) {
        setDone(true);
        notifications.show({
          title: 'Mot de passe modifié',
          message: 'Vous pouvez vous connecter.',
          color: getAccentColor(),
        });
      } else {
        notifications.show({
          title: 'Erreur',
          message: data.message || 'Réinitialisation impossible',
          color: 'red',
        });
      }
    } catch {
      notifications.show({
        title: 'Erreur',
        message: 'Impossible de joindre le serveur',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  });

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.hero}>
        <Box className={classes.heroPattern} />
        <Box className={classes.heroGlow} />
        <Box className={classes.heroContent}>
          <Title order={1} size={36} fw={700} lh={1.15}>
            JFL Contrôle
          </Title>
        </Box>
      </Box>

      <Box className={classes.formSide}>
        <Paper className={classes.formCard} radius="lg" p="xl" shadow="md" withBorder>
          <Stack gap="lg">
            <div>
              <Title order={2} size="h3">
                Nouveau mot de passe
              </Title>
              <Text c="dimmed" size="sm" mt={4}>
                Choisissez un nouveau mot de passe pour votre compte.
              </Text>
            </div>

            {mobileBlocked ? (
              <MobileUnsupportedNotice />
            ) : null}

            {!mobileBlocked && !token && (
              <Alert color="red" variant="light" title="Lien incomplet">
                Ce lien n&apos;est pas valide.{' '}
                <Anchor component={Link} to="/">
                  Demandez une réinitialisation
                </Anchor>
                .
              </Alert>
            )}

            {!mobileBlocked && done ? (
              <Stack gap="md">
                <Alert color={getAccentColor()} variant="light" title="C&apos;est fait">
                  Votre mot de passe a été mis à jour.
                </Alert>
                <Button onClick={() => navigate('/')}>Retour à la connexion</Button>
              </Stack>
            ) : !mobileBlocked ? (
              <form onSubmit={handleSubmit}>
                <Stack gap="md">
                  <PasswordInput
                    label="Nouveau mot de passe"
                    placeholder="••••••••"
                    radius="md"
                    size="md"
                    autoComplete="new-password"
                    disabled={!token}
                    {...form.getInputProps('password')}
                  />
                  <PasswordInput
                    label="Confirmer le mot de passe"
                    placeholder="••••••••"
                    radius="md"
                    size="md"
                    autoComplete="new-password"
                    disabled={!token}
                    {...form.getInputProps('confirm')}
                  />
                  <SaveButton
                    size="md"
                    radius="md"
                    loading={loading}
                    fullWidth
                    disabled={!token}
                  />
                  <Anchor
                    component={Link}
                    to="/"
                    size="sm"
                    style={{ alignSelf: 'center' }}
                  >
                    <Group gap={6} component="span" wrap="nowrap">
                      <IconArrowLeft size={14} />
                      Retour à la connexion
                    </Group>
                  </Anchor>
                </Stack>
              </form>
            ) : null}
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
